import * as React from 'react';

import { useMutation } from '@apollo/client';

import {
  AddToWishlistMutation,
  AddToWishlistMutationVariables,
  RemoveFromWishlistMutation,
  RemoveFromWishlistMutationVariables,
} from '@notino/shared/definitions/types';

import getItemDataByIdQuery from '@components/Universals/ProductItem/queries/itemDataById.graphql';
import variantFragment from '@containers/ProductDetailContainer/queries/fragCatalogVariant.graphql';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import addToWishlistMutation from '../queries/addToWishlist.graphql';
import removeFromWishlistMutation from '../queries/removeFromWishlist.graphql';
import { trackExponeaWishlistUpdate } from '../tracking/exponea';
import {
  handleTrackWishlistOperation,
  WishlistActionLocation,
} from '../tracking/gtm';
import { updateWishlistedCache } from '../updateWishlistedCache';

import { useIsWishlistEnabled } from './useIsWishlistEnabled';

export const publishUpdateWishlistCountEvent = () => {
  if (window.wishlistService) {
    window.wishlistService.count();
  } else if (document) {
    // publish a custom event only when the wihlistService is unavailable because
    // when it is available the event is published inside the wishlistService count() method
    const event = new CustomEvent('header:wishlist:overview:update');
    document.dispatchEvent(event);
  }
};

interface IUseWishlistActions {
  productId: string;
  wishlisted: boolean;
  onCompleted?: (productId: string, wishlisted: boolean) => void;
}

export const useWishlistActionsFactory = ({
  onCompleted,
}: Pick<IUseWishlistActions, 'onCompleted'>) => {
  const { getTimeFromInit } = useTrackingContext();

  const isWishlistAllowed = useIsWishlistEnabled();

  const [add, { loading: addLoading }] = useMutation<
    AddToWishlistMutation,
    AddToWishlistMutationVariables
  >(addToWishlistMutation);

  const [remove, { loading: removeLoading }] = useMutation<
    RemoveFromWishlistMutation,
    RemoveFromWishlistMutationVariables
  >(removeFromWishlistMutation);

  const loading = addLoading || removeLoading;

  const handleWishlistClick = React.useCallback(
    async ({
      productId,
      wishlisted,
      actionLocation,
      trackingPromoLabels = [],
      listName,
      listPosition,
    }: {
      productId: string;
      wishlisted: boolean;
      actionLocation: WishlistActionLocation;
      trackingPromoLabels?: string[];
      listName?: string;
      listPosition?: number;
    }) => {
      const addedToWishlist = !wishlisted;
      const operation = addedToWishlist ? add : remove;
      const { data } = await operation({
        variables: { productId },
        update: (cache) => {
          updateWishlistedCache({
            cache,
            wishlisted: addedToWishlist,
            productId,
          });

          const itemDataById = cache.readQuery({
            query: getItemDataByIdQuery,
            variables: { id: productId.toString() },
          })?.vpProductById;

          const variantData = cache.readFragment({
            fragment: variantFragment,
            id: `CatalogVariant:${productId}`,
          });

          const product = itemDataById || variantData;

          if (product) {
            handleTrackWishlistOperation({
              addedToWishlist: addedToWishlist,
              product: { ...product, wishlisted: addedToWishlist },
              timing: getTimeFromInit(),
              actionLocation,
              additionalPromoLabels: trackingPromoLabels,
              additionalGtm: {
                list_name: listName,
                list_position: listPosition,
              },
            });
          }
        },
      });

      const wishlistProductIds =
        'WishlistAddProduct' in data
          ? data.WishlistAddProduct.products
          : data.WishlistRemoveProduct.products;

      trackExponeaWishlistUpdate({
        action: addedToWishlist ? 'add' : 'remove',
        productId: Number(productId),
        wishlistProductIds: wishlistProductIds.map(Number),
      });

      publishUpdateWishlistCountEvent();
      onCompleted?.(productId, addedToWishlist);
    },
    [add, onCompleted, remove, getTimeFromInit]
  );

  return React.useMemo(
    () => ({
      handleWishlistClick,
      isWishlistAllowed,
      loading,
    }),
    [handleWishlistClick, isWishlistAllowed, loading]
  );
};

export const useWishlistActions = ({
  productId,
  wishlisted,
  onCompleted,
}: IUseWishlistActions) => {
  const { handleWishlistClick, isWishlistAllowed, loading } =
    useWishlistActionsFactory({ onCompleted });

  return React.useMemo(
    () => ({
      handleWishlistClick: (
        actionLocation: WishlistActionLocation,
        trackingPromoLabels: string[] = []
      ) => {
        return handleWishlistClick({
          productId,
          wishlisted,
          actionLocation,
          trackingPromoLabels,
        });
      },
      isWishlistAllowed,
      loading,
    }),
    [handleWishlistClick, isWishlistAllowed, loading, productId, wishlisted]
  );
};
